import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { getServerErrorLevel, RECENT_ERRORS_RANGE, statsLink } from '@/config/httpStats';
import { formatNumber } from '@/utils/format';
import type { DomainHttpStats } from '@/types/stats';
import { RateBadge } from './RateBadge';
import { StatusChip } from './StatusChip';
import './stats.css';

export interface HttpErrorsCardProps {
  /** Sites with 5xx responses, most first; the card is hidden when empty */
  domains: DomainHttpStats[];
}

/**
 * Dashboard card listing the sites with the most server errors in the last
 * hour, linking to their statistics
 */
export function HttpErrorsCard({ domains }: HttpErrorsCardProps) {
  if (domains.length === 0) return null;

  return (
    <Card variant="bordered" padding={false} className="http-errors-card">
      <CardHeader className="http-errors-card-header">
        <CardTitle as="h2">HTTP errors (last hour)</CardTitle>
        <Link to={statsLink({ range: RECENT_ERRORS_RANGE })} className="http-errors-card-link">
          View statistics
        </Link>
      </CardHeader>
      <CardContent className="http-errors-card-content">
        <ul className="http-errors-list">
          {domains.map((site) => {
            const topServerError = site.top_statuses.find((item) => item.status >= 500);
            return (
              <li key={site.domain} className="http-errors-item">
                <Link
                  to={statsLink({ range: RECENT_ERRORS_RANGE, domain: site.domain })}
                  className="http-errors-domain"
                >
                  {site.domain}
                </Link>
                <span className="http-errors-count">{formatNumber(site.status_classes['5xx'])} × 5xx</span>
                <RateBadge rate={site.error_rate} level={getServerErrorLevel(site.error_rate)} label="5xx rate" />
                {topServerError && <StatusChip status={topServerError.status} count={topServerError.count} />}
              </li>
            );
          })}
        </ul>
      </CardContent>
    </Card>
  );
}
